/**
 * Configuration des environnements
 * Ce fichier centralise toutes les variables d'environnement et leur validation
 */

export interface AppConfig {
  mode: 'development' | 'staging' | 'production';
  app: {
    name: string;
    version: string;
    baseUrl: string;
  };
  api: {
    baseUrl: string;
    baseUrlLegacy: string;
    timeout: number;
  };
  msal: {
    clientId: string;
    scopes: string;
    domainHint: string;
    authority: string;
    desktopRedirectUri: string;
    mobileRedirectUri: string;
  };
  features: {
    enableDebug: boolean;
    enableLogs: boolean;
    enableMockData: boolean;
  };
  fullCalendar: {
    licenseKey: string;
  };
  aurion: {
    urlTemplate: string;
    absencesOneCoursePageId: string;
    absencesSeveralCoursesPageId: string;
  };
  analytics: {
    matomoUrl: string;
    matomoSiteId: string;
  };
  applicationInsight: {
    licenseKey: string;
  };
}

/**
 * Récupère et valide la configuration de l'environnement actuel
 */
function getConfig(): AppConfig {
  const requiredEnvVars = ['VITE_MSAL_CLIENT_ID', 'VITE_API_BASE_URL', 'VITE_APP_BASE_URL'];

  // Vérification des variables d'environnement requises
  for (const envVar of requiredEnvVars) {
    if (!import.meta.env[envVar]) {
      throw new Error(`Variable d'environnement manquante: ${envVar}`);
    }
  }

  return {
    mode: (import.meta.env.VITE_APP_MODE || 'development') as AppConfig['mode'],
    app: {
      name: import.meta.env.VITE_APP_NAME || 'EMN Ward beta',
      version: import.meta.env.VITE_APP_VERSION || '0.0.1',
      baseUrl: import.meta.env.VITE_APP_BASE_URL,
    },
    api: {
      baseUrl: import.meta.env.VITE_API_BASE_URL,
      baseUrlLegacy: import.meta.env.VITE_API_BASE_URL_LEGACY,
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000'),
    },
    msal: {
      clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
      scopes: import.meta.env.VITE_MSAL_SCOPES || '',
      domainHint: import.meta.env.VITE_MSAL_DOMAIN_HINT || '',
      authority: import.meta.env.VITE_MSAL_AUTHORITY || '',
      desktopRedirectUri: import.meta.env.VITE_MSAL_DESKTOP_REDIRECT_URI || '',
      mobileRedirectUri: import.meta.env.VITE_MSAL_MOBILE_REDIRECT_URI || '',
    },
    features: {
      enableDebug: import.meta.env.VITE_ENABLE_DEBUG === 'true',
      enableLogs: import.meta.env.VITE_ENABLE_LOGS === 'true',
      enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
    },
    fullCalendar: {
      licenseKey: import.meta.env.VITE_APP_FULL_CALENDAR_LICENSE_KEY || '',
    },
    aurion: {
      urlTemplate: import.meta.env.VITE_AURION_URL_TEMPLATE || '',
      absencesOneCoursePageId: import.meta.env.VITE_AURION_ABSENCES_ONE_COURSE_PAGE_ID || '',
      absencesSeveralCoursesPageId:
        import.meta.env.VITE_AURION_ABSENCES_SEVERAL_COURSES_PAGE_ID || '',
    },
    analytics: {
      matomoUrl: import.meta.env.VITE_MATOMO_URL || '',
      matomoSiteId: import.meta.env.VITE_MATOMO_SITE_ID || '',
    },
    applicationInsight: {
      licenseKey: import.meta.env.VITE_APP_APPLICATIONINSIGHTS_CONNECTION_STRING || '',
    },
  };
}

// Configuration globale de l'application
export const config = getConfig();

// Fonctions utilitaires
export const isDevelopment = () => config.mode === 'development';
export const isStaging = () => config.mode === 'staging';
export const isProduction = () => config.mode === 'production';

// Logger conditionnel basé sur l'environnement
export const logger = {
  log: (...args: any[]) => {
    if (config.features.enableLogs) {
      console.log(...args);
    }
  },
  warn: (...args: any[]) => {
    if (config.features.enableLogs) {
      console.warn(...args);
    }
  },
  error: (...args: any[]) => {
    if (config.features.enableLogs) {
      console.error(...args);
    }
  },
  debug: (...args: any[]) => {
    if (config.features.enableDebug) {
      console.debug(...args);
    }
  },
};

export default config;
